import { TemplateRef } from '@angular/core';
import { MenuItem } from 'primeng/api';

export class Column {
  id: string = ''; //unique id for the column, used for customizing columns and other features
  fields?: any[] = []; //for multi field purpose. eg. date range

  header: string = '';
  headerTranslationParams?: string[] = []; //pass variables to translated text
  headerConfig?: HeaderConfig = new HeaderConfig();

  hide?: boolean = false; //optional property to indicate if the column should be hidden (eg. id)
  customizeHide?: boolean = false; //optional property to indicate if the column has been set by user to be hidden (customize feature)

  sticky?: boolean = false; //optional property to indicate if the column is sticky
  displayType?:
    | 'normal'
    | 'multiline'
    | 'chips'
    | 'chipsWithExtra'
    // | 'chipsOrText'
    | 'innerHtml'
    | 'status'
    | 'file'
    | 'image'
    | 'imageWithText'
    | 'custom' = 'normal'; //optional property to indicate how to display data
  checked?: boolean = true; //for customizing hide/show columns in listing page
  customTemplate?: TemplateRef<any> = new TemplateRef();

  inlineEditConfigV2?: InlineEditConfigV2 | null = null; //for inline edit v2 tables

  wrapText?: boolean = false;
  elipsis?: boolean = false; //optional property to indicate if text should be truncated with ellipsis
  tooltipProp?: string = '';

  //request
  reqDisplayType?: 'text' | 'textarea' | 'chips' | 'radio' | 'toggle-active' | 'toggle-enabled' | 'hyperlink' | 'custom' | 'status' = 'text';
  hasChangesFn?: ( // custom comparison logic for change table
    newValue: any,
    oldValue: any
  ) => boolean;

  constructor(init?: Partial<Column>) {
    Object.assign(this, init);
  }
}

export class PaginationData {
  searchValue: string = '';
  currentPage: number = 0;
  pageSize: number = 10;
  totalPages: number = 0;
  totalRecords: number = 0;
  // first: number = 0;
  // last: number = 0;
}

export class concurrentUsers {
  totalActiveUsers: number = 0; //total number of active users currently using the system
  totalLicenseUsers: number = 0; //total number of license users allowed
}

//global class for table data
export class TableInfo {
  showActionContainer: boolean = true; //toggle action container (filter, customize, search bar)
  type: 'listing' | 'inline-edit' | 'nested' | 'nested-alt' | 'nested-alt-child' | 'req-changes' = 'listing'; //table type - listing: basic, inline-edit: allows for fields to be updated, nested: can expand rows to show nested rows, nested-alt: child has header and different styling, nested-alt-child: child table for nested-alt
  columns: Column[] = []; //table columns
  frozenColumns: Column[] = []; //future enhancement of frozen columns
  showCheckbox: boolean = true; //true: show checkbox, false: hide checkbox
  autoDetectDisplayType: boolean = true; //whether to auto detect display type based on value (eg. array -> chips)
  customEmptyMessage?: string | null = null; //custom empty message to show when there is no data, if null it will use default message
  concurrentUsers: concurrentUsers = new concurrentUsers(); //license thing
  customizeColumns: boolean = false; //toggle whether user can customize columns or not

  showPagination: boolean = true; // toggle whether the pagintion can be shown or not
  paginationData: PaginationData = new PaginationData(); //pagination data

  deleteOn: 'off' | 'default' | 'condition' = 'default'; //delete condition: turn off deleting/show delete by default/condition based deleting
  conditionalDeleteProp: string = 'isNew'; //the property to check for isConditionalDelete
  conditionalDeleteValue: any = true; //value that allows deleting

  showFooterBtns: boolean = false; //whether to show footer buttons (eg. save, cancel) for inline edit tables
  footerBtns: FooterBtnConfig[] = [];

  showIcon: 'edit' | 'more' | 'delete' | 'end' | 'none' | 'view' | 'rollback' = 'none'; //icon type for action column
  showSecondaryIcon = this.showIcon; //icon type for secondary action column

  moreOptions: MenuItem[] = [];
  moreOptionsType: 'whole' | 'item-based' = 'whole'; //options for mode (three dots) button

  enableRowClick: boolean = true; //toggle whether row can be clicked or not
  highlightOnRowClick?: boolean = false; // the row will stay highlight when click

  showSelectedCount?: boolean = false; //whether to show count of selected items when using checkboxes
  showItemCount?: boolean = false; //whether to show count of items in table

  expandableProp: string = 'isExpandable'; //FOR NESTED: dynamic property to check if row is expandable
  childrenProp: string = 'children'; //for recursive tables - property to send into nested table
  checkboxStateMapProp: string = 'id';
  useCheckboxProMax: boolean = false;
  checkboxProMaxProps?: string[] = []; // e.g. ['code', 'name', 'effectiveDate']

  constructor(init?: Partial<TableInfo>) {
    Object.assign(this, init);
  }
}

export class HeaderConfig {
  showInfoTooltip: boolean = false;
  infoTooltipText: string = '';
}

export class FooterBtnConfig {
  key: string = '';
  label: string = '';
  isPrimary: boolean = false;
  showWhenEmpty: boolean = false;

  constructor(init?: Partial<FooterBtnConfig>) {
    Object.assign(this, init);
  }
}

//#region Table Edit V2
export class TableInfoEdit extends TableInfo {
  editType: 'mode-based' | 'row-based' = 'mode-based'; //when to trigger edit mode - perRow: on clicking edit button
  nestedChildProp: string = 'children';
  showCancelOnPageChange: boolean = true;

  constructor(init?: Partial<TableInfoEdit>) {
    super();
    Object.assign(this, init);
    this.checkboxStateMapProp ??= 'id';
  }
}

export class InlineEditConfigV2 { //for inline edit tables
  fieldType: 'text'
    | 'dropdown'
    | 'popup'
    | 'date'
    | 'daterange'
    | 'percentage'
    | 'toggle'
    | 'number' //digit grouping: no, decimal: no
    | 'qty' //digit grouping: yes, decimal: no
    | 'price' //digit grouping: yes, decimal: yes
    | 'number-format-left'
    | 'numberOf' = 'text';
  dropdownOptions: any[] = []; //for editType 'dropdown', for general options which is used by the whole column
  getDropdownOptions?: (row: any) => any[]; //for options per item/row
  dropdownValueProp: string | null = null; //for input ngmodel/formcontrol shet

  editOn: 'off' | 'default' | 'condition' = 'default'; //edit condition: turn off editing/edit by default/condition based editing
  conditionalEditProp: string = 'isNew'; //the property to check to allow editing if editOn = 'condition'
  conditionalEditValue: any = true; //value that allows editing

  suffix?: string = ''; //suffix to show after number input

  constructor(init?: Partial<InlineEditConfigV2>) {
    Object.assign(this, init);
  }
}

//#region DEPRECATED

// tableInfo {
// editMode: 'row' | 'cell' = 'row'; //edit mode for inline edit tables
// editOnClick?: boolean = true; //for inline edit tables

// editOn: 'view' | 'clickBtn' = 'clickBtn'; //when to trigger edit mode - view: on view, clickBtn: on clicking edit button
// isDeletableOnNewOnly?: boolean = false; //only allow deleting when creating new item, not allow deleting existing item
// isDeletableOnNewOnlyProp?: string = 'isNew'; //the property to check for isDeletableOnNewOnly
// }

// column {
// readonly?: boolean = false; //optional property to indicate if the column is read-only
// inlineEditConfig?: InlineEditConfig = new InlineEditConfig(); //for inline edit tables
// isEditable?: boolean = false; //for inline edit tables: whether the column is editable or not
// isEditableOnConditionOnly?: boolean = false; //for inline edit tables: only allow editing when creating new item, not allow editing existing item
// isEditableOnConditionOnlyProp?: string = 'isNew'; //the property to check for isEditableOnNewOnly
// isEditableOnConditionOnlyValue?: any = true; //value that allows editing
// }

// export class InlineEditConfig {
//   editType?: 'text' | 'dropdown' | 'popup' | 'date' | 'daterange' | 'percentage' | 'toggle' | 'number' | 'price' | 'numberOf' = 'text'; //for inline edit tables
//   dropdownOptions?: any[] = []; //for editType 'dropdown', for general options which is used by the whole column
//   getDropdownOptions?: (item: any) => any[]; //for options per item/row
//   // dropdownOptionsByItem?: boolean = false;
//   // dropdownOptionsProperty?: string = '';
//   inputModelProp?: string | null = null; //for input ngmodel/formcontrol shet
//   popupComponent?: any = null; //for editType 'popup'
// }
